import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Mail, Sparkles, Send, Shield, Zap } from "lucide-react";
import DemoSection from "@/components/DemoSection";

const plans = [
  { id: "starter", name: "Starter", price: 3, quota: 100, features: ["100 emails / month", "AI email generation", "Gmail integration", "Email history"] },
  { id: "pro", name: "Pro", price: 5, quota: 200, featured: true, features: ["200 emails / month", "Everything in Starter", "Priority AI model", "Priority support"] },
  { id: "scale", name: "Scale", price: 10, quota: 500, features: ["500 emails / month", "Everything in Pro", "Bulk workflows", "Early access to features"] },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F8F9FA] text-slate-900">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_jd-mailer-pro/artifacts/25t7dqsa_Untitled%20design%20%2835%29.png" alt="Aplica" className="h-8 w-8 rounded-md" />
            <span className="font-display font-semibold text-lg">Aplica</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="#how" className="hover:text-slate-900">How it works</a>
            <a href="#pricing" className="hover:text-slate-900">Pricing</a>
            <a href="#faq" className="hover:text-slate-900">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/auth" className="text-sm text-slate-600 hover:text-slate-900 px-4 py-2" data-testid="nav-login">Log in</Link>
            <Link to="/auth?mode=signup" className="btn-primary text-sm" data-testid="nav-signup">Get started <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-lines-bg opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="label-xs">[01] Freelancer Outreach - Automated</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-medium tracking-tighter leading-[1.05] text-slate-900">
              Paste a JD.<br />
              Get a <span className="bg-[#FFED4A] px-2">personalized</span> email.<br />
              Send from your Gmail.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl font-normal leading-relaxed">
              The fastest way for freelancers to respond to job descriptions. Aplica writes a tailored subject & body, detects the hirer's email and sends it through your own inbox - so the replies land with you.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/auth?mode=signup" className="btn-primary" data-testid="hero-cta">
                Start free - 10 emails <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#pricing" className="px-6 py-3 rounded-md border border-slate-200 bg-white text-slate-900 font-medium hover:bg-slate-50 transition-colors">
                See pricing
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-[#10B981]" /> Your Gmail · your sender identity</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-[#10B981]" /> We never read replies</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#0047FF]/5 rounded-2xl -rotate-2" />
              <img
                src="https://images.unsplash.com/photo-1745910020846-3d4d0088d24d?crop=entropy&cs=srgb&fm=jpg&w=900&q=85"
                alt="Minimalist desk"
                className="relative rounded-xl border border-slate-200 w-full h-[420px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-lg p-4 shadow-sm max-w-xs">
                <div className="label-xs">Subject</div>
                <div className="font-display text-sm mt-1">Senior React dev - 6 yrs, shipped SaaS for YC startups</div>
                <div className="mt-2 text-xs text-slate-500">sent via yourname@gmail.com · 2s ago</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-20 border-t border-slate-200">
        <span className="label-xs">[02] How it works</span>
        <h2 className="mt-3 text-3xl md:text-4xl font-display tracking-tight">Three steps to every reply.</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { n: "01", icon: Mail, t: "Paste the JD", d: "Copy any job description from Upwork, LinkedIn, Twitter, Discord - anywhere. We auto-detect the hirer's email if it's in the text." },
            { n: "02", icon: Sparkles, t: "AI writes it", d: "Our AI reads the JD + your profile and drafts a focused subject line and personalised body. Edit anything inline." },
            { n: "03", icon: Send, t: "Send from your Gmail", d: "Connect Gmail once. Messages are sent from your own address via the Gmail API and not from a shared pool. Replies come directly to you." },
          ].map((s) => (
            <div key={s.n} className="tech-card p-8">
              <div className="flex items-start justify-between">
                <s.icon className="h-6 w-6 text-[#0047FF]" />
                <span className="label-xs">{s.n}</span>
              </div>
              <h3 className="mt-6 text-xl font-display font-medium">{s.t}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Live demo */}
      <DemoSection />

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-20 border-t border-slate-200">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="label-xs">[04] Pricing</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-display tracking-tight"> Costs as low as 0.03$ Per mail.</h2>
          </div>
          <p className="text-slate-600 max-w-md">Simple monthly plans. Cancel anytime. Every plan includes AI generation, Gmail sending and email history.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div
              key={p.id}
              className={`tech-card p-8 flex flex-col ${p.featured ? "border-[#0047FF] border-2 relative" : ""}`}
              data-testid={`pricing-card-${p.id}`}
            >
              {p.featured && <span className="absolute -top-3 left-8 bg-[#0047FF] text-white text-xs px-2.5 py-1 rounded-full font-medium">Most popular</span>}
              <div className="label-xs">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-5xl font-display font-medium tracking-tighter">${p.price}</span>
                <span className="text-slate-500">/month</span>
              </div>
              <div className="mt-1 text-slate-600">{p.quota} emails included</div>
              <ul className="mt-6 space-y-2 text-sm text-slate-600 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 text-[#10B981] mt-0.5" /> {f}</li>
                ))}
              </ul>
              <Link
                to="/auth?mode=signup"
                className={`mt-8 text-center py-3 rounded-md font-medium transition-colors ${
                  p.featured ? "bg-[#0047FF] text-white hover:bg-[#003BE3]" : "bg-white border border-slate-200 text-slate-900 hover:bg-slate-50"
                }`}
                data-testid={`pricing-cta-${p.id}`}
              >
                Get {p.name}
              </Link>
            </div>
          ))}
        </div>
      </section>
      {/* Early Bird Banner */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="rounded-2xl bg-[#0047FF] text-white px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-wider opacity-80">
              Limited Time Offer
            </p>
            <h3 className="text-2xl font-display mt-1">
              Get the Core Plan for just $1 for your first month 🚀
            </h3>
            <p className="mt-2 text-white/80">
              Use code <span className="font-bold">EARLYBIRD66</span> at checkout.
            </p>
          </div>
      
          <div className="bg-white text-[#0047FF] px-4 py-2 rounded-lg font-semibold">
            Code: EARLYBIRD66
          </div>
        </div>
      </section>
                 

      {/* Trust */}
      <section id="faq" className="max-w-7xl mx-auto px-6 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { i: Shield, t: "Send-only access", d: "We can only send. We cannot read your inbox." },
            { i: Zap, t: "From your inbox", d: "Emails are sent through your own Gmail, preserving deliverability & your sender reputation." },
            { i: Mail, t: "Replies go to you", d: "We don't intercept or track responses. Your conversation lives in Gmail." },
          ].map((x, i) => (
            <div key={i} className="flex gap-4">
              <div className="h-10 w-10 rounded-md bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                <x.i className="h-5 w-5" />
              </div>
              <div>
                <h4 className="font-display font-medium">{x.t}</h4>
                <p className="text-sm text-slate-600 mt-1">{x.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white mt-20">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <h2 className="text-4xl md:text-6xl font-display tracking-tighter">Ready to stop writing cold emails from scratch?</h2>
          <Link to="/auth?mode=signup" className="inline-flex items-center gap-2 mt-8 bg-white text-slate-900 px-6 py-3 rounded-md font-medium hover:bg-slate-100 transition-colors" data-testid="footer-cta">
            Get started <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap justify-between gap-4 text-sm text-white/60">
            <span>© {new Date().getFullYear()} Aplica</span>
            <div className="flex items-center gap-6">
              <Link to="Privacy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms of Service</Link>
              <a href="mailto:aplica1410@gmail.com" className="hover:text-white">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
